import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Info } from 'lucide-react'
import { ConfirmModal, type ConfirmModalData } from '@/components/ui/ConfirmModal'
import { apiError } from '@/pages/settings/datacenter/shared'
import { portalApi, type LegacyCompany } from './portal-api'

/**
 * Eski bulut (SaaS) kullanıcıları için "Pusula artık masaüstünde" bildirimi.
 * Firma yöneticisi taşıma adımlarını ve durumunu görür; taşıma bitince bulut verisinin silinmesini isteyebilir.
 */
export function MigrationNotice({ companies, hasLicense }: { companies: LegacyCompany[]; hasLicense: boolean }): JSX.Element {
  const { t } = useTranslation('portal')
  const qc = useQueryClient()
  const [confirm, setConfirm] = useState<ConfirmModalData | null>(null)
  const adminOf = companies.filter((c) => c.isAdmin)
  const requestDeletion = useMutation({
    mutationFn: (tenantId: string) => portalApi.post('/migration/deletion-request', { tenantId }),
    onSuccess: () => { toast.success(t('migration.deletionRequested')); void qc.invalidateQueries({ queryKey: ['portal-me'] }) },
    onError: (e) => toast.error(apiError(e)),
  })

  return (
    <div className="glass-card border-primary/30 bg-primary/5 p-5">
      <div className="flex gap-3">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="w-full space-y-3 text-sm">
          <p className="font-semibold text-foreground">{t('migration.title')}</p>
          <p className="text-muted-foreground">{t('migration.intro', { companies: companies.map((c) => c.name).join(', ') })}</p>
          {adminOf.length > 0 ? (
            <ol className="list-decimal space-y-1 pl-5 text-foreground">
              <li>{hasLicense ? t('migration.stepLicense') : t('migration.stepLicensePending')}</li>
              <li>{t('migration.stepDownload')}</li>
              <li>{t('migration.stepInstall')}</li>
              <li>{t('migration.stepMigrate')}</li>
              <li>{t('migration.stepInvite')}</li>
            </ol>
          ) : (
            <p className="text-foreground">{t('migration.member')}</p>
          )}
          {adminOf.map((c) => (
            <div key={c.tenantId} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/50 px-3 py-2">
              <span className="text-foreground">{c.name}</span>
              <span className="text-xs text-muted-foreground">
                {t(`migration.status.${c.migrationStatus ?? 'NONE'}`)}
                {c.deletionStatus && ` · ${t(`migration.deletion.${c.deletionStatus}`)}`}
              </span>
              {c.migrationStatus === 'COMPLETED' && !c.deletionStatus && (
                <button type="button" className="btn-ghost text-xs text-destructive" onClick={() => setConfirm({
                  title: t('migration.deleteTitle'), message: t('migration.deleteMessage', { company: c.name }), danger: true,
                  confirmLabel: t('migration.deleteConfirm'), onConfirm: () => requestDeletion.mutate(c.tenantId),
                })}>{t('migration.deleteButton')}</button>
              )}
            </div>
          ))}
        </div>
      </div>
      <ConfirmModal data={confirm} onClose={() => setConfirm(null)} />
    </div>
  )
}
